import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/lembra/Logo";
import { LogoScrollMorph } from "@/components/lembra/LogoScrollMorph";
import { PageMeta } from "@/components/lembra/PageMeta";
import { Bell, ChevronLeft, ChevronRight, Search,
  // S2 nav-dropdown leaf + category icons. Lightweight semantic
  // pictures, Lucide line-art only, no fills.
  Briefcase, NotebookPen, Building2, Rocket,
  Cake, Hand, Sparkles, Trophy, Globe2,
  Users,
  CalendarDays,
  // MS56 mobile-nav: hamburger trigger + sheet close + accordion chevron.
  Menu, X, ChevronDown,
} from "lucide-react";
import { NavDropdown, type NavDropdownCategory } from "@/components/lembra/NavDropdown";
import {
  CATERING_SITE_ORIGIN,
  LANDING_CATERING_CATEGORIES,
} from "@/data/landingCateringNav";
import { HowLembraWorks } from "@/components/lembra/HowLembraWorks";
import { CmdKProvider, useCmdK } from "@/components/lembra/CmdKPalette";
// MS50: session-aware nav. Authed visitors land on the marketing
// page sometimes (deep link, 404 redirect, "back to home" from
// chat). Pre-MS50 the nav still showed "Login" — confusing because
// the session was actually still valid; clicking the logo from
// /login would carry them straight back to /dashboard. The fix:
// surface the session-aware destination in the nav itself.
import { useAuth } from "@/hooks/useAuth";
// MS192: chatHref appends ?sessionId=<id> when an active hero
// conversation is in flight, so clicking a "Send a gift" CTA picks
// up where the buyer left off instead of resetting to a fresh chat.
// useHeroSessionId is the React-subscribing version — Index needs
// it so the CTA's `to` prop re-renders when the buyer starts a
// conversation in the embedded hero (storage write happens deep in
// useInlineConversation; no React state would otherwise propagate).
import { chatHref, useHeroSessionId } from "@/lib/heroSession";
// S7c: StatsAndCloser split into StatsSection + FinalCTA, with
// BeliefBelt's editorial frame absorbed into StatsSection. Old
// import retained as a comment in case the combined component
// gets revived; actual file kept on disk for the same reason.
// import { StatsAndCloser } from "@/components/lembra/StatsAndCloser";
import { StatsSection } from "@/components/lembra/StatsSection";
import { FinalCTA } from "@/components/lembra/FinalCTA";
import { MultiVendorShowcase } from "@/components/lembra/MultiVendorShowcase";
import { VariantB } from "@/components/hero/variants/VariantB";
import { AnimatedReveal } from "@/components/hero/shared/AnimatedReveal";
// DessertShowcase removed from this page (see comment above its prior
// render slot). Component file retained for potential future reuse.
// import { DessertShowcase } from "@/components/lembra/DessertShowcase";
// CaseSection removed from this page (S5 absorbed its stats into
// StatsAndCloser). Component file retained for potential reuse on a
// /research or /why-lembra page.
// import { CaseSection } from "@/components/lembra/CaseSection";
// MS211: DayInAugust is no longer mounted on the landing — the
// "Day with Lembra" timeline was retired when the hero rev landed
// (VariantB took over the editorial slot). The component file
// stays on disk for potential reuse on a future /tour or /how-
// it-works longform surface; matching the same convention as
// CaseSection + BeliefBelt above. Re-commenting the active import
// drops the unused symbol from Index's dependency graph cleanly.
// import { DayInAugust } from "@/components/lembra/DayInAugust";
// BeliefBelt absorbed into StatsSection in S7c; component file
// kept on disk for potential reuse on a /research page.
// import { BeliefBelt } from "@/components/lembra/BeliefBelt";
import { CateringTeaser } from "@/components/lembra/CateringTeaser";
// Pricing data (TEAM_SIZE_OPTIONS / formatPricing / BIG_DAYS_STARTING_PRICE
// / TeamSizeBucket) was used by the now-removed pricing section (S6a).
// orderData.ts is kept intact, those exports are still consumed by the
// /order flow itself. Only the landing-page import is dropped.

// Sprint 2.5a A.0/B/S6a, pricing-related data deleted from this page.
// `teamSizes` (derived from TEAM_SIZE_OPTIONS) and `planDefinitions`
// (Sweet & Fun / Bit Fancy / Go All Out tier metadata) drove the now-
// removed pricing section. They have no other consumers in
// Index.tsx. Kept as a tripwire comment in case a future agent
// greps for them, the data still lives in src/components/order/
// orderData.ts for the actual /order flow.

// Smart Calendar + Team Dashboard were merged into "Team At A Glance", they
// were saying the same thing twice (auto-tracked dates + an at-a-glance view
// of what's coming up and what's been celebrated). Notifications was added
// because prospects keep asking "does this actually tell me when an order is
// placed / out for delivery / coming up?", yes, but the landing didn't say so.
// featureIcons / features were used by the removed features grid (see
// SHOW_PRODUCT_SHOWCASE = false). Kept as references in git history.

const productScreenshots = [
  { src: "/product-screenshots/dashboard.png", alt: "Lembra Dashboard, team stats, upcoming celebrations, recent activity", label: "Dashboard", desc: "See who's coming up, what's been celebrated, and how the budget's tracking." },
  { src: "/product-screenshots/calendar.png", alt: "Celebration Calendar, birthdays and anniversaries auto-tracked", label: "Calendar", desc: "Every birthday and work anniversary, auto-tracked on one calendar." },
  // Cache-buster on notifications.png: the file was added in #85 but #84
  // already referenced the path, so any browser that loaded the carousel
  // between those two deploys cached a 404. The query string forces a
  // fresh fetch and bypasses that stale negative cache.
  { src: "/product-screenshots/notifications.png?v=2", alt: "Notifications panel, real-time updates when orders are placed, confirmed, and out for delivery", label: "Notifications", desc: "A ping when orders are placed, confirmed, and out for delivery, plus a heads-up before every celebration." },
  { src: "/product-screenshots/team.png", alt: "Team Management, dietary needs, start dates, birthday tracking", label: "Team", desc: "Names, dietary needs, start dates, all in one place." },
  { src: "/product-screenshots/orders.png", alt: "Orders, track every celebration from pending to delivered", label: "Orders", desc: "Track every order from pending to delivered." },
];

// `external: true` flags links that escape the React Router app, currently
// just /catering, which is a static HTML file served from /public. The footer
// renderer below switches to a plain <a> for those so the browser does a real
// navigation instead of client-side routing into a 404.
type FooterLink = { label: string; href: string; external?: boolean };
const footerLinks: FooterLink[][] = [
  // MS30: Pricing link added back. The S6a "no pricing on landing"
  // call still holds — pricing isn't on the landing page itself —
  // but its own /pricing surface is now public so B2B buyers who
  // need cost signal before signing up can find it.
  // MS405: Team, Changelog, Brand removed — internal/sensitive pages not for public footer.
  [{ label: "About", href: "/about" }, { label: "Vendors", href: "/vendors" }, { label: "Pricing", href: "/pricing" }, { label: "Blog", href: "/blog" }, { label: "Catering", href: "https://catering.lembra.com.au", external: true }],
  [{ label: "Contact", href: "/contact" }, { label: "Security", href: "/security" }, { label: "Privacy", href: "/privacy" }, { label: "Terms", href: "/terms" }, { label: "Partner with us", href: "/partner-apply" }],
];

/* ─── Shared section heading style (DM Sans bold, matches original design) ─── */
const h2Style: React.CSSProperties = {
  fontSize: '32px',
  letterSpacing: '-0.01em',
};

/* ─── Section visibility flags (Sprint 2.5a A.0/B landing rev) ────
   Lucky's call: hide several large sections that no longer match the
   chat-concierge positioning. Flags rather than removed JSX so each
   section is a one-line revival away if we change our minds, and the
   section's source stays under git history right where it was.

   To revive any of these: flip the flag to `true`. To audit what each
   one shows, search for the flag name; the gated `<section>` follows
   immediately. */
// Features grid was fully replaced rather than flag-wrapped, so no
// flag here, see the HIDDEN comment block where it used to render.
// SHOW_PRICING_SECTION removed in S6a (pricing permanently dropped
// per Lucky's call: don't anchor users to fixed prices).
const SHOW_PRODUCT_SHOWCASE = false;    // "See it in action" navy block

/** Shared class for centre-pill nav items (anchor buttons + Browse link). */
const NAV_PILL_ITEM_CLASS =
  "rounded-full px-3 py-2 md:px-3.5 lg:px-5 lg:py-2 text-sm lg:text-[15px] font-medium text-navy bg-transparent border-0 cursor-pointer transition-all hover:bg-white/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy/40 whitespace-nowrap";

/* ─── S2 nav dropdown content ──────────────────────────────────────
   "Built for", two ways into the product, by audience and by moment.
   Both ultimately route into existing surfaces:
     • By role: each leaf routes to /chat with a brief tailored to
       that role. The chat concierge picks up from there.
     • By moment: each leaf routes to /browse with the occasion
       prefilled (the SmartIntentBar's same routing slot).
   No dead pages, no fake "coming soon" landings, every leaf hits
   a real surface. */
const BUILT_FOR_CATEGORIES: NavDropdownCategory[] = [
  {
    id: "role",
    label: "By role",
    icon: Users,
    items: [
      {
        label: "HR & people ops",
        icon: Briefcase,
        href: "/chat?brief=I%20run%20people%20ops%20and%20want%20to%20set%20up%20our%20team%27s%20celebration%20programme",
      },
      {
        label: "Office manager",
        icon: Building2,
        href: "/chat?brief=I%20manage%20our%20office%20and%20want%20to%20organise%20team%20celebrations",
      },
      {
        label: "Executive assistant",
        icon: NotebookPen,
        href: "/chat?brief=I%27m%20an%20EA%20and%20want%20to%20organise%20celebrations%20for%20my%20team",
      },
      {
        label: "Founder / leadership",
        icon: Rocket,
        href: "/chat?brief=I%20lead%20a%20team%20and%20want%20to%20make%20sure%20celebrations%20get%20handled",
      },
    ],
  },
  {
    id: "moment",
    label: "By moment",
    icon: CalendarDays,
    items: [
      { label: "Birthdays", icon: Cake, href: "/browse?occasion=birthday" },
      { label: "Welcomes", icon: Sparkles, href: "/browse?occasion=welcome" },
      { label: "Farewells", icon: Hand, href: "/browse?occasion=farewell" },
      { label: "Work anniversaries", icon: Trophy, href: "/browse?occasion=anniversary" },
      { label: "Cultural events", icon: Globe2, href: "/browse?occasion=cultural" },
    ],
  },
];

/* ─── Slide image w/ graceful fallback ────────────────────────────
   Real product screenshots live in /public/product-screenshots. If a
   PNG is missing (e.g. the freshly-added notifications.png hasn't been
   captured yet), we render an inline mock card instead of a broken
   image. Keeps the deck whole and the slide self-explanatory until the
   real screenshot is dropped in. */
function ProductSlideImage({
  src, alt, label, active, eager, kenBurns,
}: {
  src: string;
  alt: string;
  label: string;
  active: boolean;
  eager?: boolean;
  kenBurns?: boolean;
}) {
  const [errorCount, setErrorCount] = useState(0);
  // Bypass URL: appended after the first onError to dodge any cached 404
  // (browser HTTP cache can hold a 404 from before the asset was deployed
  // and serve the cached miss even after the file goes live). One retry
  // with a query string is enough to bypass, if it still 404s, fall back.
  const effectiveSrc = errorCount === 0
    ? src
    : `${src}${src.includes("?") ? "&" : "?"}retry=${errorCount}`;
  const baseStyle: React.CSSProperties = {
    opacity: active ? 1 : 0,
    transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
    animation: kenBurns && active ? 'kenBurns 4.5s ease-out forwards' : 'none',
  };
  if (errorCount >= 2) {
    return (
      <div
        className="absolute inset-0 w-full h-full flex flex-col items-center justify-center text-center px-6 bg-gradient-to-br from-cream via-white to-butter/40"
        style={baseStyle}
        aria-label={alt}
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-coral/10 mb-3">
          <Bell size={22} className="text-coral" />
        </div>
        <p className="text-navy font-semibold text-base">{label} preview</p>
        <p className="mt-1 text-charcoal/60 text-xs max-w-xs">
          Screenshot coming soon, drop a PNG into <code className="text-charcoal/80">/public{src.split("?")[0]}</code> and it'll appear here.
        </p>
      </div>
    );
  }
  return (
    <img
      key={effectiveSrc}
      src={effectiveSrc}
      alt={alt}
      className="absolute inset-0 w-full h-full object-cover object-top"
      style={baseStyle}
      loading={eager ? "eager" : "lazy"}
      onError={() => setErrorCount((n) => n + 1)}
    />
  );
}

export default function Landing() {
  const [scrolled, setScrolled] = useState(false);
  // teamSize state removed in S6a, was only consumed by the now-removed
  // pricing tier section.
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" ? window.innerWidth < 768 : false);
  // MS192: subscribe to the active hero sessionId so the "Send a gift"
  // CTA re-renders the moment the embedded VariantB conversation gets
  // one (otherwise the CTA's `to` prop captures null at first render
  // and never updates, sending the buyer to a fresh /chat).
  useHeroSessionId();

  // MS50: session-aware nav. When the visitor is signed in we show
  // "Dashboard" instead of "Sign in" (and the primary CTA still routes
  // to /chat — that's the right action whether you're authed or not).
  // Wait for auth hydrate + getUser() validation so stale localStorage
  // JWTs never flash "Dashboard" before the server rejects them.
  const { user, session, loading } = useAuth();
  // Require both session + user (same bar as Login redirect) so a stale
  // JWT blob can't flash "Dashboard" before getUser() clears it.
  const isAuthed = !loading && !!session && !!user;

  // MS56: mobile nav sheet state. The hamburger button only shows
  // sub-md (md:hidden) so the existing centred pill keeps the
  // desktop look; below md we collapse the nav into a full-screen
  // sheet with all destinations (otherwise mobile visitors saw only
  // Logo + Send CTA with no way to navigate anywhere).
  const [mobileOpen, setMobileOpen] = useState(false);

  // Lock body scroll while the mobile sheet is open so the page
  // underneath doesn't scroll behind it.
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  // Esc closes the sheet — universal modal convention.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  // MS30 update: /pricing IS now a real route (its own dedicated
  // public page) but it doesn't scroll to a #pricing anchor on
  // landing — that anchor was permanently dropped in S6a per
  // Lucky's call ("don't anchor users to fixed prices on the
  // hero scroll"). Anyone hitting /pricing lands on the
  // standalone Pricing page directly.
  // Single slide index for both mobile and desktop, keeping them in sync
  // also means dropping a viewport between sizes doesn't reset the carousel.
  const [activeSlide, setActiveSlide] = useState(0);
  const [slideUserPaused, setSlideUserPaused] = useState(false);
  const slideResumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Use matchMedia (single change event) instead of resize spam, the old
  // listener fired on every resize tick which forced a re-render of this
  // 900+ line component.
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // rAF-throttled scroll listener that only calls setState when the
  // boolean actually flips, avoids re-rendering Landing on every pixel.
  useEffect(() => {
    let queued = false;
    const apply = () => {
      queued = false;
      setScrolled((prev) => {
        const next = window.scrollY > 60;
        return prev === next ? prev : next;
      });
    };
    const onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(apply);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* ── Showcase auto-advance: 4.5 s per slide, paused after manual nav.
       Desktop gets a slightly longer dwell than the old 3 s mobile loop
       because there's more to look at on a wider screenshot. The pause
       window resumes 8 s after the user last touched it, long enough
       to read but short enough that abandoned interactions still loop. */
  useEffect(() => {
    if (slideUserPaused) return;
    const id = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % productScreenshots.length);
    }, 4500);
    return () => clearInterval(id);
  }, [slideUserPaused]);

  const goToSlide = (idx: number) => {
    setActiveSlide(((idx % productScreenshots.length) + productScreenshots.length) % productScreenshots.length);
    setSlideUserPaused(true);
    if (slideResumeTimerRef.current) clearTimeout(slideResumeTimerRef.current);
    slideResumeTimerRef.current = setTimeout(() => setSlideUserPaused(false), 8000);
  };
  const nextSlide = () => goToSlide(activeSlide + 1);
  const prevSlide = () => goToSlide(activeSlide - 1);

  // Jumps past the hero scroll spacer then smoothly scrolls to target section
  const jumpTo = (id: string) => {
    const target = document.getElementById(id);
    if (!target) return;
    const navHeight = 80;
    const targetTop = target.getBoundingClientRect().top + window.scrollY - navHeight;
    window.scrollTo({ top: targetTop, behavior: "smooth" });
  };

  return (
    <CmdKProvider>
      <PageMeta
        title="Lembra, workplace celebrations, sorted by a chat concierge."
        description="Lembra remembers every workplace celebration. Tell our concierge who's celebrating; we curate cakes, hampers, flowers, vouchers and more from Melbourne's best vendors and handle delivery. Pay per gift, no subscription."
        path="/"
      />
    {/* MS-mobile / MS356: safe-area-bottom padding lifted to App.tsx as
        the canonical wrapper so EVERY route gets the iOS Safari URL-bar
        / Android Chrome tab-strip clearance, not just Index. The
        min-h-screen here stays because Landing relies on it for the
        gradient backgrounds + section anchoring. */}
    <div className="min-h-screen">
      {/* Skip link for keyboard users */}
      <a
        href="#how-it-works"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:bg-navy focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:text-sm focus:font-medium focus:shadow-lg"
      >
        Skip to main content
      </a>

      {/* Nav */}
      {/* Sprint 2.5a A.0/B (landing rev), pill-style nav per Lucky's
          design exploration screenshot. Three structural slots:
            • Left:   Lembra logo (existing rounded-pill navy chip)
            • Middle: rounded pill container holding 3 nav anchors
                      ("How it works", "Catering", "Stories"). Pricing
                      removed per Lucky's ask, the pricing section
                      itself is also hidden in this PR. Login moved
                      out of the pill (it's a low-volume action that
                      belongs subordinated to the right of the CTA).
            • Right:  primary coral CTA "Send a gift" + small Login
                      link.
          Mobile collapses to logo + Send CTA (no pill). The mid pill
          uses translucent navy/8 background that gets a touch denser
          on scroll, matching the existing scrolled-blur nav rhythm. */}
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 animate-slide-down ${
          scrolled ? "shadow-sm" : ""
        }`}
        style={{
          backdropFilter: scrolled ? "blur(12px)" : "none",
          WebkitBackdropFilter: scrolled ? "blur(12px)" : "none",
          backgroundColor: scrolled ? "rgba(250,246,240,0.88)" : "transparent",
          borderBottom: scrolled ? "1px solid rgba(29,52,97,0.07)" : "none",
        }}
      >
        {/* MS170 mobile-fix: the original 3-column grid
            (`1fr auto 1fr`) wasted the centre column on sub-md
            where the pill is `hidden md:flex` (display:none) — the
            grid still split the row 50/50 between the two `1fr`
            columns, pushing the right cluster's `justify-end` to
            land at the 50% mark (centre of the viewport, crowding
            the logo). Sub-md now uses a 2-column grid
            (`1fr auto`): the logo column expands, the CTA +
            hamburger cluster sits flush right. md+ keeps the
            original 3-column shape so the pill stays dead-centre. */}
        <div
          className="grid items-center gap-x-6 px-6 py-4 grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]"
          style={{
            maxWidth: '1280px',
            margin: '0 auto',
          }}
        >
          {/* Left, logo.
              Sprint L (2026-05-20): swapped the static <Logo /> for
              the scroll-morph wordmark per the "devour, refined"
              design (handoff bundle lembra-mascot/, May 2026). The
              new component renders "lembra." as typographic DOM so
              the period can grow into a navy disc with a butter
              italic l as the page scrolls past 60px (and unwinds
              below 40px on scroll-back). Static <Logo> stays in
              every non-landing surface — see Logo.tsx for the
              rendering split. */}
          <div className="flex items-center">
            <LogoScrollMorph />
          </div>

          {/* Center, pill container (How it works · Browse · Built for · Catering). MS11:
              pill now appears from md breakpoint (was lg), so the
              tablet half-screen / iPad-portrait / large-phone-
              landscape range no longer renders as just "logo + Send"
              floating with empty centre. At md the pill uses tighter
              padding (px-3 py-1.5) so it fits next to the right-side
              CTAs; lg+ keeps the original generous spacing.
              Below md (true mobile) we still drop the pill entirely
              and lean on the Send CTA. */}
          <div
            className="hidden md:flex items-center justify-center gap-1 rounded-full p-1 lg:p-1.5"
            style={{
              backgroundColor: scrolled ? 'rgba(29,52,97,0.07)' : 'rgba(29,52,97,0.05)',
              border: '1px solid rgba(29,52,97,0.08)',
              transition: 'background-color 300ms',
            }}
          >
            <button
              type="button"
              onClick={() => jumpTo("how-it-works")}
              className={NAV_PILL_ITEM_CLASS}
              style={{ fontFamily: "DM Sans, sans-serif" }}
            >
              How it works
            </button>

            <Link
              to="/browse"
              className={NAV_PILL_ITEM_CLASS}
              style={{ fontFamily: "DM Sans, sans-serif" }}
            >
              <span className="lg:hidden">Catalogue</span>
              <span className="hidden lg:inline">Browse catalogue</span>
            </Link>

            <NavDropdown label="Built for" categories={BUILT_FOR_CATEGORIES} />

            <NavDropdown
              label="Catering"
              categories={LANDING_CATERING_CATEGORIES}
              topBanner={
                <a
                  href={CATERING_SITE_ORIGIN}
                  className="group flex items-center justify-between gap-3"
                  style={{ fontFamily: "DM Sans, sans-serif" }}
                >
                  <span>
                    <span
                      className="block text-[10px] uppercase tracking-[0.18em] text-coral"
                      style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                    >
                      Sister product
                    </span>
                    <span className="block text-sm font-semibold text-navy mt-0.5">
                      Lembra Catering, a separate Lembra platform
                    </span>
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-navy/70 group-hover:text-coral transition-colors">
                    Visit site
                    <ChevronRight size={12} strokeWidth={2.5} />
                  </span>
                </a>
              }
            />
          </div>

          {/* Right, search button + login + primary CTA. */}
          <div className="flex items-center justify-end gap-3">
            {/* ⌘K search button, wired in S4. Lives inside the
                CmdKProvider tree so the hook resolves to the real
                open() function. Renders as a small pill with the
                magnifier icon and ⌘K glyph (same visual as S2). */}
            <CmdKNavButton />

            {/* MS50: Sign in → Dashboard when authed. Visible from md up
                (tablet dead zone fix: hamburger is md:hidden, so this
                link must appear at md+ or 768–1023px has no sign-in entry).
                The destination + label swap means an authed visitor who
                lands here knows instantly that they're signed in. */}
            <Link
              to={isAuthed ? "/dashboard" : "/login"}
              className="hidden md:inline-block text-sm font-medium text-navy/70 hover:text-navy transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy/40 focus-visible:rounded"
              style={{ fontFamily: "DM Sans, sans-serif" }}
            >
              {isAuthed ? "Dashboard" : "Sign in"}
            </Link>
            {/* Primary CTA, coral pill. Sprint 2.5a /landing-cta-fix
                routes to /chat (was /order pre-pivot). MS192: uses
                chatHref() so an in-progress hero conversation lands
                on /chat with ?sessionId= and restores. */}
            <Link
              to={chatHref()}
              className="inline-flex items-center justify-center rounded-full bg-coral text-white font-medium transition-all hover:bg-coral/90 hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2"
              style={{
                fontFamily: "DM Sans, sans-serif",
                padding: isMobile ? '11px 20px' : '13px 26px',
                fontSize: isMobile ? '0.9rem' : '1rem',
                boxShadow: '0 2px 6px rgba(196,86,74,0.25)',
              }}
            >
              {isMobile ? 'Send' : 'Send a gift'}
            </Link>

            {/* MS56: mobile hamburger. Only visible sub-md where the
                centred pill is hidden — without this, mobile visitors
                saw only Logo + Send CTA with no way to reach How it
                works, Vendors, Pricing, About, Login, etc. Matches the
                catering platform's hamburger pattern (small navy-line
                outlined circle, lucide Menu glyph). */}
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              aria-haspopup="dialog"
              aria-expanded={mobileOpen}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral md:hidden"
              style={{ borderColor: "rgba(29,52,97,0.14)", color: "rgba(29,52,97,0.7)" }}
            >
              <Menu size={16} strokeWidth={2} />
            </button>
          </div>
        </div>
      </nav>

      {/* MS56: mobile navigation sheet. Full-screen overlay with all
          destinations the desktop pill carries + the most useful
          public-site pages from the footer. Mirrors the catering
          platform's pattern (hamburger → full-screen sheet with
          accordion expand for nested sections). Only rendered when
          mobileOpen — no DOM cost on desktop. */}
      {mobileOpen && (
        <MobileNavSheet
          onClose={() => setMobileOpen(false)}
          onAnchor={(id) => {
            setMobileOpen(false);
            // Defer the scroll so the sheet's overflow-lock unwinds
            // before the page tries to scroll.
            setTimeout(() => jumpTo(id), 50);
          }}
          isAuthed={isAuthed}
        />
      )}

      {/* Hero, Variant B: "Centered SmartIntentBar" (Sprint 2.5a A.0,
          Variant 1B). The chat input IS the hero, single column,
          editorial copy stacked above. Lucky's pick. */}
      <VariantB />

      {/* ── Trust line: hinge between hero and catalogue ─────────
          MS364: rewritten as a HINGE that visually joins the hero
          (ending in cream #FFF8E7) to the catalogue showcase
          (now starting in butter #FBF3D0 from the very top). The
          band's gradient ramps from cream → mid-butter so:
            - The top edge matches the hero's exit colour
              perfectly (no seam at the hero boundary).
            - The bottom edge matches the catalogue section's
              entry colour exactly (no seam at the catalogue
              boundary).
            - The mid-mark of the band carries the trust copy
              over a colour that's already on its way INTO the
              catalogue's palette, so the band reads as the
              opening beat of the catalogue rather than the
              tail of the hero.
          Pre-MS364 the band ended in pure white (#FFFFFF) and
          the catalogue section started in #FFF8E7 — that white-
          to-cream step was the visible seam Lucky flagged. */}
      <div
        style={{
          background: "linear-gradient(180deg, #FFF8E7 0%, #FBF3D0 100%)",
        }}
      >
        <div
          className="flex items-center justify-center gap-3 sm:gap-4 px-6"
          style={{
            margin: "0 auto",
            paddingTop: "clamp(36px, 6.5vw, 72px)",
            paddingBottom: "clamp(36px, 6.5vw, 72px)",
            maxWidth: 720,
          }}
        >
          <span
            aria-hidden="true"
            className="hidden sm:inline-block"
            style={{ width: 28, height: 1, background: "rgba(29,52,97,0.22)", flexShrink: 0 }}
          />
          <p
            className="text-center"
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: "clamp(0.72rem, 1.7vw, 0.78rem)",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "rgba(29,52,97,0.78)",
              fontWeight: 500,
              lineHeight: 1.4,
              margin: 0,
            }}
          >
            Curated catalogue · Concierge handled
          </p>
          <span
            aria-hidden="true"
            className="hidden sm:inline-block"
            style={{ width: 28, height: 1, background: "rgba(29,52,97,0.22)", flexShrink: 0 }}
          />
        </div>
      </div>

      {/* ── Multi-vendor catalogue showcase (S6b) ─────────────────
          Replaces the slot the original cake-only DessertShowcase
          used to occupy. */}
      <MultiVendorShowcase />

      {/* ── CaseSection removed (Sprint 2.5a A.0/B/S5) ────────────
          The same three research stats (23% / 9× / 31%) are now
          presented in the bottom-of-page <StatsAndCloser /> as a
          Heidi-pattern card row with animated tickers. Removing
          here avoids the same numbers appearing twice. The
          CaseSection component file is kept intact at
          src/components/lembra/CaseSection.tsx for potential reuse
          on a /research or /why-lembra page. */}

      {/* ── How it works, Heidi scroll-shell pattern (S3) ───────
          Replaces the prior 3-step inline navy timeline. New
          component lives at src/components/lembra/HowLembraWorks.tsx
          and renders a click-driven (auto-advancing) 4-phase shell
         , Brief / Curate / Approve / Deliver, with mockups inside
          a butter gradient frame and pill tabs beneath. Same
          navy/butter aesthetic so the page rhythm holds.

          The prior 3-step ol is preserved in git history if we want
          it back; the new shell is a strict superset (4 steps
          instead of 3, mockup per phase, tab-driven). */}
      <HowLembraWorks id="how-it-works" />
      {/* MS14 cleanup: the dead `{false && (...)}` tripwire block
          (~175 lines of pre-S3 inline 3-step navy timeline) has been
          deleted. It was preserved for reference but the new
          <HowLembraWorks /> shell above is a strict superset; the
          old structure lives in git history at PR #162 head if a
          future reader needs to reconstruct it. Removing it also
          fixes the `no-constant-binary-expression` lint error that
          was blocking CI on every PR since S3. */}

      {/* ── Features grid HIDDEN (Sprint 2.5a A.0/B landing rev) ──
          Lucky's call: hide the six-piece feature card grid. The new
          chat-concierge positioning makes the feature taxonomy
          ("Team At A Glance / Always In The Loop / One-Click Orders /
          Dietary Smarts / Curated Vendors / Set and Forget") feel like
          a SaaS dashboard pitch when the brand promise is now a
          quietly-handled concierge. Keeping the data structure
          (`features`, `featureIcons` consts at top of file), they
          drive nothing else right now but cost ~0 to leave; nothing
          else needs them either, and re-introducing the section is a
          one-paste away if we ever want it back.

          To revive: paste the prior <section> back in here. Git
          history has the full block at PR #158 head. */}

      {/* ── BeliefBelt removed (S7c) ──────────────────────────────
          BeliefBelt's italic Newsreader headline + DM Sans
          subordinate now live as the editorial frame inside the
          new <StatsSection /> below. One coherent "why it matters"
          beat instead of two adjacent ones. The component file
          (src/components/lembra/BeliefBelt.tsx) is kept for
          potential reuse on a /research or /why-lembra page. */}

      {/* ── Product Showcase ("See it in action"), HIDDEN by flag ──
          Lucky's call: hide. The block is a navy-background screenshot
          carousel with mobile/desktop variants showing the dashboard,
          calendar, notifications panel, team page, and orders. Six
          screenshots, ~200 lines of carousel orchestration. Hidden
          rather than removed because the carousel is real
          infrastructure we may want back when the product surface
          stabilises. Flip SHOW_PRODUCT_SHOWCASE at the top of this
          file to revive. */}
      {SHOW_PRODUCT_SHOWCASE && (
      <section
        className="py-16 lg:py-24"
        style={{ position: "relative", zIndex: 3, background: "#1D3461" }}
      >
        <div className="px-6" style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <AnimatedReveal animation="fadeInUp" duration={700}>
            <h2
              className="mb-3 text-center font-sans font-bold text-white"
              style={h2Style}
            >
              See it in action.
            </h2>
            <p className="mb-10 text-center text-white/60 text-lg">
              Everything your team needs, in one calm, considered dashboard.
            </p>
          </AnimatedReveal>

          {/* ── Mobile: Auto-advancing screenshot carousel ── */}
          <div className="md:hidden">
            <AnimatedReveal animation="fadeInUp" duration={600} delay={100}>
              {/* Browser chrome mockup */}
              <div className="rounded-xl overflow-hidden" style={{ background: '#151f36' }}>
                {/* Chrome bar */}
                <div className="flex items-center gap-2 px-3 py-2.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-white/20" />
                    <div className="w-2.5 h-2.5 rounded-full bg-white/20" />
                    <div className="w-2.5 h-2.5 rounded-full bg-white/20" />
                  </div>
                  <div className="flex-1 mx-3">
                    <div className="bg-white/10 rounded px-2.5 py-1 text-[11px] text-white/40 text-center">
                      app.lembra.com.au
                    </div>
                  </div>
                  {/* Active tab label */}
                  <span className="text-[11px] font-medium text-white/60 pr-1">
                    {productScreenshots[activeSlide].label}
                  </span>
                </div>

                {/* Screenshot with Ken Burns zoom + cross-fade */}
                <div className="relative overflow-hidden" style={{ aspectRatio: '16/10', background: '#f4f0e8' }}>
                  {productScreenshots.map((s, i) => (
                    <ProductSlideImage
                      key={s.label}
                      src={s.src}
                      alt={s.alt}
                      label={s.label}
                      active={activeSlide === i}
                      eager={i === 0}
                      kenBurns
                    />
                  ))}
                </div>
              </div>

              {/* Description, just show the active slide's text */}
              <p className="mt-3 text-sm text-white/70 text-center leading-relaxed min-h-[44px] px-2 transition-opacity duration-300">
                {productScreenshots[activeSlide].desc}
              </p>

              {/* Dot navigation */}
              <div className="flex justify-center gap-2 mt-4">
                {productScreenshots.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => goToSlide(i)}
                    className="rounded-full transition-all duration-300"
                    style={{
                      width: activeSlide === i ? '20px' : '7px',
                      height: '7px',
                      background: activeSlide === i ? '#fff' : 'rgba(255,255,255,0.55)',
                    }}
                    aria-label={`View ${productScreenshots[i].label}`}
                  />
                ))}
              </div>

              {/* Progress bar */}
              <div className="mt-3 mx-auto rounded-full overflow-hidden" style={{ width: '80px', height: '2px', background: 'rgba(255,255,255,0.15)' }}>
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${((activeSlide + 1) / productScreenshots.length) * 100}%`,
                    background: 'rgba(255,255,255,0.6)',
                    transition: 'width 0.4s ease',
                  }}
                />
              </div>
            </AnimatedReveal>
          </div>

          {/* ── Desktop: Browser mockup, auto-advancing with prev/next arrows + tab pills.
                The tab pills double as direct-jump nav AND the "you are here" indicator.
                Arrows give a slideshow feel; auto-advance loops the deck. */}
          <div className="hidden md:block">
            <AnimatedReveal animation="fadeInUp" duration={600} delay={100}>
              <div className="flex justify-center gap-2 mb-8 flex-wrap">
                {productScreenshots.map((s, i) => (
                  <button
                    key={s.label}
                    onClick={() => goToSlide(i)}
                    className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                      activeSlide === i
                        ? "bg-white text-navy shadow-md"
                        : "bg-white/10 text-white/70 hover:bg-white/20 hover:text-white"
                    }`}
                    aria-pressed={activeSlide === i}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </AnimatedReveal>

            <AnimatedReveal animation="fadeInUp" duration={700} delay={200}>
              <div className="relative mx-auto" style={{ maxWidth: '960px' }}>
                {/* Browser chrome */}
                <div className="flex items-center gap-2 px-4 py-3 rounded-t-xl bg-[#151f36]">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-white/20" />
                    <div className="w-3 h-3 rounded-full bg-white/20" />
                    <div className="w-3 h-3 rounded-full bg-white/20" />
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="bg-white/10 rounded-md px-3 py-1.5 text-xs text-white/40 text-center">
                      app.lembra.com.au / {productScreenshots[activeSlide].label.toLowerCase()}
                    </div>
                  </div>
                </div>
                {/* Screenshot stack */}
                <div className="relative overflow-hidden rounded-b-xl bg-cream" style={{ aspectRatio: '16/10' }}>
                  {productScreenshots.map((s, i) => (
                    <ProductSlideImage
                      key={s.label}
                      src={s.src}
                      alt={s.alt}
                      label={s.label}
                      active={activeSlide === i}
                      eager={i === 0}
                    />
                  ))}
                </div>

                {/* Prev / next arrow controls, positioned outside the frame so
                    they don't sit on top of UI screenshots. Hover to grow. */}
                <button
                  onClick={prevSlide}
                  aria-label="Previous screenshot"
                  className="hidden lg:flex absolute top-1/2 -translate-y-1/2 -left-16 h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white border border-white/20 hover:bg-white hover:text-navy hover:scale-105 transition-all duration-300 backdrop-blur"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={nextSlide}
                  aria-label="Next screenshot"
                  className="hidden lg:flex absolute top-1/2 -translate-y-1/2 -right-16 h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white border border-white/20 hover:bg-white hover:text-navy hover:scale-105 transition-all duration-300 backdrop-blur"
                >
                  <ChevronRight size={20} />
                </button>

                {/* On medium screens (no room for outside arrows), tuck arrows
                    into the bottom edge as a compact pill. */}
                <div className="lg:hidden flex items-center justify-center gap-2 mt-5">
                  <button
                    onClick={prevSlide}
                    aria-label="Previous screenshot"
                    className="h-9 w-9 flex items-center justify-center rounded-full bg-white/10 text-white border border-white/20 hover:bg-white hover:text-navy transition-all duration-300"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={nextSlide}
                    aria-label="Next screenshot"
                    className="h-9 w-9 flex items-center justify-center rounded-full bg-white/10 text-white border border-white/20 hover:bg-white hover:text-navy transition-all duration-300"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>

                {/* Soft glow halo */}
                <div className="absolute -inset-4 -z-10 rounded-2xl bg-white/5 blur-xl" />
              </div>

              {/* Active slide caption + progress dots underneath the frame */}
              <div className="mt-6 text-center">
                <p className="text-sm text-white/70 leading-relaxed max-w-xl mx-auto min-h-[40px]">
                  {productScreenshots[activeSlide].desc}
                </p>
                <div className="flex justify-center gap-2 mt-4">
                  {productScreenshots.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => goToSlide(i)}
                      className="rounded-full transition-all duration-300"
                      style={{
                        width: activeSlide === i ? '24px' : '8px',
                        height: '8px',
                        background: activeSlide === i ? '#fff' : 'rgba(255,255,255,0.45)',
                      }}
                      aria-label={`View ${productScreenshots[i].label}`}
                    />
                  ))}
                </div>
              </div>
            </AnimatedReveal>
          </div>
        </div>
      </section>
      )}

      {/* ── Pricing section permanently removed (S6a) ──
          Lucky's direction: do not surface fixed prices on the
          landing page. "Anchoring users to fixed price tiers
          would limit their scope on understanding what we are
          capable of doing, I want them to feel open-ended."
          Pricing surfaces in the order/chat flow at the point
          of intent (where it actually means something).
          Cake-themed tier names (Sweet & Fun / Bit Fancy /
          Go All Out) and the team-size selector were both part
          of the removed block; both still live in /order if we
          ever want to revisit. */}

      {/* ── Stats section (S7c, combines BeliefBelt's editorial
          frame with the three research stat cards from S5). Cream-
          to-butter gradient bg differentiates from CateringTeaser's
          pure cream below. */}
      <StatsSection />

      {/* ── Catering teaser (sister product, second-to-last per
          Lucky's call). Pure cream bg, distinct from StatsSection's
          gradient above so the two adjacent light sections register
          as separate beats. */}
      <CateringTeaser />

      {/* ── Final CTA (S7c, restored as a separate section, navy-
          themed to bookend HowLembraWorks above). The page rhythm
          now reads butter → cream → navy → cream → cream → navy →
          footer, giving the bottom half real structure instead of
          three adjacent cream blocks. */}
      <FinalCTA />

      {/* Footer, MS13 unified with FinalCTA above. Both share #1D3461;
          the seam between them is invisible now that FinalCTA's
          oversized bottom padding has been collapsed. The 40px top
          padding here gives the footer columns just enough air below
          the CTA buttons without re-introducing the "two slabs" look. */}
      <footer className="bg-navy" style={{ padding: '40px 0 36px', position: "relative", zIndex: 3 }}>
        <div className="px-6" style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Mobile: stacked layout. Desktop: row layout */}
          <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
            <Logo size="sm" onDark />
            <div className="grid grid-cols-2 gap-x-12 gap-y-6 md:flex md:gap-12">
              {footerLinks.map((col, i) => (
                <ul key={i} className="space-y-2.5">
                  {col.map((link) => (
                    <li key={link.label}>
                      {link.external ? (
                        <a href={link.href} className="text-sm text-white/70 hover:text-white transition-colors">{link.label}</a>
                      ) : (
                        <Link to={link.href} className="text-sm text-white/70 hover:text-white transition-colors">{link.label}</Link>
                      )}
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          </div>
          <div className="mt-10 border-t border-white/10 pt-6 flex flex-col items-center gap-3">
            <div className="flex items-center gap-4">
              <a
                href="https://www.instagram.com/lembra_au"
                target="_blank" rel="noopener noreferrer"
                aria-label="Lembra on Instagram"
                className="text-white/70 hover:text-white transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/company/lembra-celebrations/"
                target="_blank" rel="noopener noreferrer"
                aria-label="Lembra on LinkedIn"
                className="text-white/70 hover:text-white transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z" />
                  <rect x="2" y="9" width="4" height="12" />
                  <circle cx="4" cy="4" r="2" />
                </svg>
              </a>
            </div>
            <p className="text-xs text-white/50">© 2026 Lembra. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
    </CmdKProvider>
  );
}

/** ⌘K nav button — ported from lembra-catering.com SiteHeader so the
 *  trigger reads in the same visual dialect as the catering-pattern
 *  palette PR #610 landed. MS460's "subtle navy" variant was bigger
 *  (px-4 py-2), desktop-only (hidden lg:), and rendered ⌘K as a plain
 *  span — felt off once the palette itself moved to the catering
 *  language. This block is a near-verbatim port of catering's button:
 *  tighter pill (px-2 py-1.5), kbd-styled glyph with subtle navy tint,
 *  visible from md: breakpoint, JS hover for color crossfade. */
function CmdKNavButton() {
  const { open } = useCmdK();
  const NAVY = "#1D3461";
  const NAVY_70 = "rgba(29,52,97,0.7)";
  const NAVY_LINE = "rgba(29,52,97,0.14)";
  return (
    <button
      type="button"
      onClick={open}
      aria-label="Open search (Cmd+K)"
      className="hidden md:inline-flex items-center gap-2 rounded-full px-2 py-1.5 transition-colors sm:px-2.5"
      style={{
        color: NAVY_70,
        border: `1px solid ${NAVY_LINE}`,
        background: "rgba(255,255,255,0.55)",
      }}
      onMouseEnter={(e) => {
        const t = e.currentTarget;
        t.style.borderColor = NAVY;
        t.style.color = NAVY;
        t.style.background = "rgba(255,255,255,0.85)";
      }}
      onMouseLeave={(e) => {
        const t = e.currentTarget;
        t.style.borderColor = NAVY_LINE;
        t.style.color = NAVY_70;
        t.style.background = "rgba(255,255,255,0.55)";
      }}
    >
      <Search className="h-3.5 w-3.5" aria-hidden="true" strokeWidth={2.25} />
      <kbd
        className="hidden h-5 items-center justify-center rounded px-1.5 text-[10.5px] sm:inline-flex"
        style={{
          background: "rgba(29,52,97,0.06)",
          color: NAVY_70,
          fontFamily: "'JetBrains Mono', monospace",
          border: "1px solid rgba(29,52,97,0.08)",
        }}
      >
        ⌘K
      </kbd>
    </button>
  );
}

/**
 * MobileNavSheet — MS56 full-screen mobile navigation overlay.
 *
 * Why this exists:
 *   Pre-MS56 the public landing nav on sub-md viewports rendered as
 *   just Logo + Send CTA. The centred pill (How it works / Built for
 *   / Catering) is `hidden md:flex`, the Sign-in link was formerly
 *   `hidden lg:inline-block` (768–1023px dead zone: no hamburger,
 *   no sign-in), and the ⌘K button is `hidden lg:inline-flex`.
 *   Result: mobile visitors had ZERO navigation surface to reach
 *   Vendors, Pricing, About, Team, Blog, Brand, or even Sign in.
 *
 *   This component is the catering-platform mirror: a hamburger
 *   trigger on the right cluster opens this sheet, which carries
 *   every destination a desktop visitor sees plus the most-useful
 *   public-site pages. Full-screen overlay (not a slide-in drawer)
 *   because the screen is small enough that taking the whole canvas
 *   is the most readable option.
 *
 * Layout:
 *   - Header: logo (clickable home) + close button (X)
 *   - Body: flat links + accordions for Built for / Catering mega-menus
 *   - Footer: Browse catalogue (outline) + Send a gift + Sign in/Dashboard
 *   - The desktop ⌘K palette doesn't surface here — mobile keyboards
 *     don't have a meta key.
 */
function MobileNavSheet({
  onClose,
  onAnchor,
  isAuthed,
}: {
  onClose: () => void;
  onAnchor: (id: string) => void;
  isAuthed: boolean;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Site navigation"
      className="fixed inset-0 z-[60] flex flex-col bg-cream"
      style={{
        animation: "mobileNavIn 200ms cubic-bezier(0.16, 1, 0.3, 1)",
      }}
    >
      {/* ── Header: logo + close ── */}
      <header
        className="flex items-center justify-between px-5 py-4"
        style={{ borderBottom: "1px solid rgba(29,52,97,0.08)" }}
      >
        <Logo size="sm" />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close menu"
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral"
          style={{ borderColor: "rgba(29,52,97,0.14)", color: "rgba(29,52,97,0.7)" }}
        >
          <X size={16} strokeWidth={2} />
        </button>
      </header>

      {/* ── Body: nav items ── */}
      <div className="flex-1 overflow-y-auto px-5 py-3">
        {/* How it works — scrolls to landing #how-it-works section */}
        <MobileNavItem
          label="How it works"
          onClick={() => onAnchor("how-it-works")}
        />

        <MobileNavLink label="Browse catalogue" to="/browse" onClick={onClose} />

        <MobileNavLink label="Vendors" to="/vendors" onClick={onClose} />
        <MobileNavLink label="Pricing" to="/pricing" onClick={onClose} />

        <MobileNavMegaSection
          label="Built for"
          categories={BUILT_FOR_CATEGORIES}
          onClose={onClose}
        />

        <MobileNavMegaSection
          label="Catering"
          categories={LANDING_CATERING_CATEGORIES}
          onClose={onClose}
          external
          eyebrow="Sister product"
        />

        {/* MS405: Team + Brand removed from mobile nav — internal pages. */}
        <MobileNavLink label="About" to="/about" onClick={onClose} />
        <MobileNavLink label="Blog" to="/blog" onClick={onClose} />
      </div>

      {/* ── Footer: browse + primary CTA + auth-aware link + utility row ── */}
      <footer
        className="px-5 py-4"
        style={{ borderTop: "1px solid rgba(29,52,97,0.08)" }}
      >
        <Link
          to="/browse"
          onClick={onClose}
          className="block w-full rounded-full border-2 border-navy/15 bg-white/80 px-5 py-3.5 text-center text-sm font-medium text-navy transition-all hover:border-coral hover:text-coral focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2"
          style={{ fontFamily: "DM Sans, sans-serif" }}
        >
          Browse catalogue
        </Link>
        <Link
          to={chatHref()}
          onClick={onClose}
          className="mt-3 block w-full rounded-full bg-coral px-5 py-3.5 text-center text-sm font-medium text-white shadow-sm transition-all hover:bg-coral/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2"
          style={{ fontFamily: "DM Sans, sans-serif" }}
        >
          Send a gift
        </Link>
        <Link
          to={isAuthed ? "/dashboard" : "/login"}
          onClick={onClose}
          className="mt-3 block w-full rounded-full border px-5 py-3 text-center text-sm font-medium transition-colors hover:bg-navy/5"
          style={{
            fontFamily: "DM Sans, sans-serif",
            borderColor: "rgba(29,52,97,0.14)",
            color: "#1D3461",
          }}
        >
          {isAuthed ? "Dashboard" : "Sign in"}
        </Link>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-navy/55">
          {/* MS405: Changelog removed — internal release notes, not for public nav. */}
          <Link to="/contact" onClick={onClose} className="hover:text-coral" style={{ fontFamily: "DM Sans, sans-serif" }}>Contact</Link>
          <Link to="/security" onClick={onClose} className="hover:text-coral" style={{ fontFamily: "DM Sans, sans-serif" }}>Security</Link>
          <Link to="/partner-apply" onClick={onClose} className="hover:text-coral" style={{ fontFamily: "DM Sans, sans-serif" }}>Partner</Link>
        </div>
      </footer>

      <style>{`
        @keyframes mobileNavIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          [aria-label="Site navigation"] { animation: none !important; }
        }
      `}</style>
    </div>
  );
}

/** Accordion mega-menu for Built for / Catering (mobile sheet). */
function MobileNavMegaSection({
  label,
  categories,
  onClose,
  external = false,
  eyebrow,
}: {
  label: string;
  categories: NavDropdownCategory[];
  onClose: () => void;
  external?: boolean;
  eyebrow?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: "1px solid rgba(29,52,97,0.08)" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between py-4 text-left"
      >
        <div>
          {eyebrow && (
            <p
              className="mb-0.5 text-[10.5px] uppercase tracking-[0.14em] text-coral/85"
              style={{
                fontFamily:
                  "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
                fontWeight: 500,
              }}
            >
              {eyebrow}
            </p>
          )}
          <span
            style={{
              fontFamily: "Newsreader, Georgia, serif",
              fontStyle: "italic",
              fontWeight: 500,
              fontSize: 22,
              color: "#1D3461",
              letterSpacing: "-0.012em",
            }}
          >
            {label}
          </span>
        </div>
        <ChevronDown
          size={16}
          strokeWidth={2}
          className={`shrink-0 text-navy/55 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="space-y-4 pb-4 pl-1">
          {categories.map((cat) => (
            <div key={cat.id}>
              <p
                className="mb-1.5 text-[10.5px] uppercase tracking-[0.14em] text-coral/85"
                style={{
                  fontFamily:
                    "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontWeight: 500,
                }}
              >
                {cat.label}
              </p>
              <div className="space-y-0.5">
                {cat.items.map((leaf) => {
                  const Icon = leaf.icon;
                  const row = (
                    <>
                      <Icon width={14} height={14} strokeWidth={1.8} className="shrink-0 text-coral" />
                      <span className="text-[14px]">{leaf.label}</span>
                    </>
                  );
                  const className =
                    "flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-navy/85 hover:bg-navy/5";
                  const style = { fontFamily: "DM Sans, sans-serif" };
                  if (external || leaf.external) {
                    return (
                      <a
                        key={leaf.href}
                        href={leaf.href}
                        onClick={onClose}
                        className={className}
                        style={style}
                      >
                        {row}
                      </a>
                    );
                  }
                  return (
                    <Link
                      key={leaf.href}
                      to={leaf.href}
                      onClick={onClose}
                      className={className}
                      style={style}
                    >
                      {row}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Direct-link row in the mobile sheet. Same visual rhythm as
 *  the accordion trigger (Newsreader italic, navy line below). */
function MobileNavLink({
  label,
  to,
  onClick,
  external,
  eyebrow,
}: {
  label: string;
  to: string;
  onClick: () => void;
  external?: boolean;
  eyebrow?: string;
}) {
  const inner = (
    <>
      <div>
        {eyebrow && (
          <p
            className="text-[10.5px] uppercase tracking-[0.14em] text-coral/85"
            style={{
              fontFamily:
                "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
              fontWeight: 500,
              marginBottom: 2,
            }}
          >
            {eyebrow}
          </p>
        )}
        <span
          style={{
            fontFamily: "Newsreader, Georgia, serif",
            fontStyle: "italic",
            fontWeight: 500,
            fontSize: 22,
            color: "#1D3461",
            letterSpacing: "-0.012em",
          }}
        >
          {label}
        </span>
      </div>
      <ChevronRight size={16} strokeWidth={2} className="text-navy/45" />
    </>
  );
  const className = "flex w-full items-center justify-between py-4 text-left";
  const style = { borderBottom: "1px solid rgba(29,52,97,0.08)" };
  if (external) {
    return (
      <a
        href={to}
        onClick={onClick}
        className={className}
        style={style}
      >
        {inner}
      </a>
    );
  }
  return (
    <Link to={to} onClick={onClick} className={className} style={style}>
      {inner}
    </Link>
  );
}

/** Button-shaped row for anchor-scroll items (How it works). */
function MobileNavItem({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between py-4 text-left"
      style={{ borderBottom: "1px solid rgba(29,52,97,0.08)" }}
    >
      <span
        style={{
          fontFamily: "Newsreader, Georgia, serif",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: 22,
          color: "#1D3461",
          letterSpacing: "-0.012em",
        }}
      >
        {label}
      </span>
      <ChevronRight size={16} strokeWidth={2} className="text-navy/45" />
    </button>
  );
}
